import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import EventFinished from "src/components/EventFinished";
import Hero from "src/components/Hero/Hero";
import TitleSection from "src/components/TitleSection/TitleSection";
import TournamentSectionalTabs from "src/components/SectionalTabs/TournamentSectionalTabs";
import TournamentSubtabsContent from "src/pages/poker/tournaments/TournamentSubtabsContent";
import { useHomepage } from "src/context/homepage/homepage.provider";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import ReactMarkdown from "react-markdown";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

const JACKPOT_API_URL =
  "https://script.google.com/macros/s/AKfycbx7RgitWFoY-Nz61fDRm1EE3z3CBbuuVbwODgcd9N9bFt-OVMz33oT2mJ0JHzoGVTbe/exec";

const CashnadoSpring2026 = () => {
  const { i18n, t } = useTranslation();
  const { tournamentsSubtabs } = useHomepage();
  const [jackpotAmount, setJackpotAmount] = useState("$100,000");

  const sectionalTabsContent = t(
    "cashnadoSpring2026.content.sectionalTabsContent",
    { returnObjects: true },
  );
  const dynamicSubTabs = sectionalTabsContent[`subTab${tournamentsSubtabs}`];

  useEffect(() => {
    const fetchJackpotAmount = async () => {
      try {
        const timestamp = new Date().getTime();
        const response = await fetch(`${JACKPOT_API_URL}?t=${timestamp}`);
        const data = await response.json();

        let amount = "$100,000"; // fallback

        // Handle numeric response
        if (typeof data === "number") {
          amount = `$${data.toLocaleString()}`;
        }
        // Handle string response
        else if (typeof data === "string") {
          // If it's already formatted, use as-is
          if (data.includes("$")) {
            amount = data;
          } else {
            // If it's a plain number string, format it
            const num = parseFloat(data.replace(/,/g, ""));
            if (!isNaN(num)) {
              amount = `$${num.toLocaleString()}`;
            } else {
              amount = data;
            }
          }
        }
        // Handle object response
        else if (data && typeof data === "object") {
          const rawValue =
            data.amount || data.value || data.jackpot || data.prize;
          if (typeof rawValue === "number") {
            amount = `$${rawValue.toLocaleString()}`;
          } else if (rawValue) {
            amount = rawValue;
          }
        }

        setJackpotAmount(amount);
      } catch (error) {
        console.error("Error fetching jackpot amount:", error);
        // Keep default fallback value on error
      }
    };

    fetchJackpotAmount();

    // Refresh every 30 seconds
    const interval = setInterval(fetchJackpotAmount, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <PageWrap>
      <Hero
        bgImage="promotions/gaming-machines/cashnado-spring-2026/cashnado-hero-image-2400x800px.jpg"
        buttonSettings={null}
        image={`promotions/gaming-machines/cashnado-spring-2026/cashnado-logo-2400px-${i18n.language}.png`}
        imageAlt="CASHNADO – Win up to $100,000!"
        descriptionBgColor="rgba(0,0,0,0)"
        frostedGlassPanel={t("cashnadoSpring2026.hero.frostedGlassPanel", {
          returnObjects: true,
        })}
      />

      <EventFinished
        scheduleKeyName="cashnadoSpring2026"
        eventType="promotion"
      />

      <MainContent>
        {/* Title Section */}
        <container-div>
          <TitleSection
            as="h1"
            title={t("cashnadoSpring2026.content.titleSection.title")}
            text={t("cashnadoSpring2026.content.titleSection.text")}
            borderColor="#cc0000"
            dark={true}
          />
        </container-div>

        {/* Progressive Jackpot Section */}
        <container-div>
          <JackpotSection
            jackpotData={t("cashnadoSpring2026.content.jackpotSection", {
              returnObjects: true,
            })}
            liveJackpotAmount={jackpotAmount}
          />
        </container-div>

        {/* How to Build Momentum Section */}
        <MomentumSection>
          <container-div>
            <TitleSection
              as="h2"
              title={t("cashnadoSpring2026.content.howToBuild.title")}
              borderColor="#cc0000"
              dark={true}
            />
            <Row className="g-4 justify-content-center">
              {(Array.isArray(
                t("cashnadoSpring2026.content.howToBuild.items", {
                  returnObjects: true,
                }),
              )
                ? t("cashnadoSpring2026.content.howToBuild.items", {
                    returnObjects: true,
                  })
                : []
              ).map((item, index) => (
                <Col key={index} xs={12} sm={6} lg={3}>
                  <MomentumCard
                    icon={item.icon}
                    title={item.title}
                    text={item.text}
                  />
                </Col>
              ))}
            </Row>
          </container-div>
        </MomentumSection>

        {/* Sectional Tabs */}
        <container-div>
          <div className="tabContent my-4 pb-4">
            <TournamentSectionalTabs
              subTabs={t("cashnadoSpring2026.content.sectionalTabs", {
                returnObjects: true,
              })}
              bgColor="#cc0000"
            />
            <TournamentSubtabsContent tournamentContent={dynamicSubTabs} />
          </div>
        </container-div>
      </MainContent>
    </PageWrap>
  );
};

// Helper component for momentum cards
const MomentumCard = ({ icon, title, text }) => {
  const cardBgImage = useResponsiveImageUrl(
    "promotions/gaming-machines/cashnado-spring-2026/cashnado_bg.jpg",
  );

  return (
    <MomentumCardWrap bgImage={cardBgImage}>
      <MomentumIconWrap>
        <ResponsiveImage src={icon} alt={title} />
      </MomentumIconWrap>
      <MomentumCardTitle>{title}</MomentumCardTitle>
      <MomentumCardText>
        <ReactMarkdown>{text}</ReactMarkdown>
      </MomentumCardText>
    </MomentumCardWrap>
  );
};

// Helper component for jackpot section
const JackpotSection = ({ jackpotData, liveJackpotAmount }) => {
  if (!jackpotData) return null;

  const bgImage = useResponsiveImageUrl(jackpotData.bgImage || "");

  return (
    <JackpotContainer bgImage={bgImage}>
      <Row className="g-0 h-100">
        <Col lg={6} md={12}>
          <JackpotTextSide>
            {jackpotData.title && (
              <JackpotTitle>
                <ReactMarkdown>{jackpotData.title}</ReactMarkdown>
              </JackpotTitle>
            )}
            {jackpotData.content && (
              <JackpotContent>
                <ReactMarkdown>{jackpotData.content}</ReactMarkdown>
              </JackpotContent>
            )}
          </JackpotTextSide>
        </Col>
        <Col
          lg={6}
          md={12}
          className="d-lg-flex align-items-center justify-content-center"
        >
          <JackpotPrizeSide>
            <JackpotPrizeAmount>{liveJackpotAmount}</JackpotPrizeAmount>
          </JackpotPrizeSide>
        </Col>
      </Row>
    </JackpotContainer>
  );
};

// Styled Components
const PageWrap = styled.div`
  width: 100%;
  height: auto;
  background-color: var(--black);
  color: var(--white);

  .hero_text_container {
    display: none;
  }

  .hero_image_container {
    padding: 20px;
  }
`;

const MainContent = styled.div`
  height: auto;
  width: 100%;
  text-align: left;

  container-div {
    background: transparent;
  }

  @media (min-width: 992px) {
    padding-bottom: 2rem;
  }

  /* Title Section Styling */
  .title-section {
    h1,
    h2 {
      color: var(--white);
      text-transform: uppercase;
    }
    p {
      color: #cccccc;

      strong {
        color: var(--white);
        /* color: #cc0000; */
      }
    }
  }

  /* Sectional Tabs Styling */
  .sectional-tournament-tabs {
    justify-content: space-around;
    margin: 20px;
    width: calc(100% - 40px);

    button {
      min-height: unset;
      padding: 0.8rem;
      background-color: rgba(255, 255, 255, 0.1);
      color: var(--white);
      border: 1px solid rgba(255, 255, 255, 0.2);

      &.active {
        background-color: #0099cc;
        color: var(--white);
        border-color: #0099cc;
      }

      &:hover {
        background-color: #0099cc;
        color: var(--white);
        border-color: #0099cc;
      }
    }

    @media (min-width: 768px) {
      button {
        max-width: 32%;
      }
    }
  }

  /* Tab Content Styling */
  .tabContent {
    background-color: var(--black);

    container-div {
      background: transparent;
    }

    p,
    li {
      color: #cccccc;
    }

    ul,
    ol {
      margin: 1rem 0;
      padding-left: 2rem;
    }

    li {
      margin-bottom: 0.5rem;
    }

    li p {
      margin: 0;
      display: inline;
    }

    table {
      color: #cccccc;
      border-color: #444;

      th {
        background-color: #0099cc;
        color: var(--white);
        border-color: #444;
      }

      td {
        border-color: #444;
      }
    }
  }
`;

const MomentumSection = styled.section`
  padding: 2.5rem 0;
  background-color: var(--lightblack);
  .title-section {
    padding: 0 0 20px 0;
  }
`;

const JackpotContainer = styled.div`
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  min-height: 400px;
  position: relative;
  ${(props) =>
    props.bgImage
      ? `
    background-image: url('${props.bgImage}');
    background-size: 54% 130%;
    background-position: right top;
    background-repeat: no-repeat;
  `
      : ""}

  .row {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 992px) {
    min-height: auto;
    ${(props) =>
      props.bgImage
        ? `
      background-size: cover;
      background-position: bottom;
    `
        : ""}
  }
`;

const JackpotTextSide = styled.div`
  background-color: transparent;
  padding: 3rem 2rem;
  padding-right: 3rem;
  margin-right: -3rem;
  height: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  @media (max-width: 992px) {
    min-height: auto;
    padding: 2rem 1.5rem;
    padding-right: 2rem;
    margin-right: 0;
    background-color: rgba(0, 0, 0, 0.85);
  }
`;

const JackpotTitle = styled.div`
  color: var(--white);
  font-weight: 700;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-transform: uppercase;

  h2,
  h3 {
    color: var(--white);
    margin: 0;
    font-size: 2rem;
    line-height: 1.2;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;

    h2,
    h3 {
      font-size: 1.5rem;
    }
  }
`;

const JackpotContent = styled.div`
  color: #cccccc;
  line-height: 1.8;
  font-size: 1rem;

  p {
    margin-bottom: 1rem;
  }

  strong {
    color: var(--white);
    font-weight: 700;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const JackpotPrizeSide = styled.div`
  padding: 3rem 2.5rem;
  height: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  @media (max-width: 992px) {
    min-height: 100px;
    padding: 2rem 1.5rem;
    background: rgba(0, 0, 0, 0.7);
  }
`;

const JackpotPrizeAmount = styled.div`
  font-size: 7rem;
  font-weight: 900;
  background: linear-gradient(180deg, #85091f 0%, #cd0000 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  -webkit-text-stroke: 2px #ffffff;
  letter-spacing: -0.02em;
  position: relative;
  z-index: 2;
  line-height: 1;


  @media (max-width: 1200px) {
    font-size: 5rem;
  }

  @media (max-width: 768px) {
    font-size: 4rem;
    -webkit-text-stroke: 1.5px #ffffff;
  }
`;

const MomentumCardWrap = styled.div`
  background: linear-gradient(145deg, #0099cc 0%, #0077aa 70%);
  border-radius: 16px;
  padding: 1rem;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  border: 2px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.bgImage
      ? `
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      background-image: url('${props.bgImage}');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      opacity: 0.1;
      z-index: 0;
      border-radius: 16px;
    }
  `
      : ""}

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 153, 204, 0.4);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const MomentumIconWrap = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: 100px;
    width: 100px;
  }
`;

const MomentumCardTitle = styled.h3`
  color: var(--white);
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
`;

const MomentumCardText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;

  p {
    margin: 0;

    strong {
      color: var(--white);
      font-weight: 700;
    }
  }
`;

export default CashnadoSpring2026;
